import React from 'react';
import { createPortal } from 'react-dom';
import { format, parseISO } from 'date-fns';

const fmtDate = (v) => {
  if (!v) return '—';
  const d = typeof v === 'string' ? parseISO(v) : new Date(v);
  return Number.isNaN(d.getTime()) ? '—' : format(d, 'd MMM yyyy');
};

const fmtDateTime = (v) => {
  if (!v) return '—';
  const d = typeof v === 'string' ? parseISO(v) : new Date(v);
  return Number.isNaN(d.getTime()) ? '—' : format(d, 'd MMM yyyy, HH:mm');
};

const fmtVehicleReportingTime = (v) => {
  if (v == null || String(v).trim() === '') return '—';
  return String(v).trim();
};

const Section = ({ title, children }) => (
  <div className="mb-6">
    <h3 className="text-sm font-semibold text-gray-700 mb-2 border-b border-gray-100 pb-1">{title}</h3>
    <div className="text-sm text-gray-900">{children}</div>
  </div>
);

const InwardDetailsModal = ({
  open,
  onClose,
  loading,
  error,
  data,
  onRetry,
}) => {
  if (!open) return null;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose?.();
  };

  const popupContent = (
    <div
      className="fixed inset-0 flex items-center justify-center p-3 md:p-4"
      style={{ zIndex: 99999 }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="inward-details-title"
    >
      <div
        className="absolute inset-0 bg-black/20"
        onClick={handleBackdropClick}
        aria-hidden="true"
      />
      <div
        className="relative w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col bg-white rounded-[18px] border border-[#EEF1F5] shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between shrink-0 px-6 pt-6 pb-2">
          <h2 id="inward-details-title" className="text-lg font-semibold text-[#101828]">
            {loading ? 'Inward Details' : `Inward Details — ${data?.transactionId || data?._id || '—'}`}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="w-7 h-7 rounded-full border border-[#D0D5DD] text-[#667085] flex items-center justify-center bg-white hover:bg-[#F6F7FA] transition"
            aria-label="Close"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <hr className="border-0 border-t border-[#EEF1F5] mt-2" />
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading && (
            <div className="py-8 text-center text-gray-500 text-sm">
              Loading...
            </div>
          )}
          {error && !loading && (
            <div className="py-8 text-center">
              <p className="text-red-600 text-sm mb-3">{error}</p>
              {onRetry && (
                <button
                  type="button"
                  onClick={onRetry}
                  className="rounded-xl border border-[#188742] bg-[#188742]/10 px-4 py-2 text-sm font-medium text-[#188742] hover:bg-[#188742]/20"
                >
                  Retry
                </button>
              )}
            </div>
          )}
          {!loading && !error && data && (
            <>
              <Section title="Basic">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-6 gap-y-1">
                  <div><span className="text-gray-500">Customer Document Number:</span> {data.invoiceNumber ?? '—'}</div>
                  <div><span className="text-gray-500">Posting Date:</span> {fmtDate(data.invoiceDate)}</div>
                  <div><span className="text-gray-500">Status:</span> {data.status ?? 'RECEIVED'}</div>
                  {/* <div><span className="text-gray-500">UOM:</span> {data.uom ?? '—'}</div> */}
                  {/* <div><span className="text-gray-500">Storage Type:</span> {data.storageType ?? '—'}</div> */}
                </div>
                <div className="mt-2">
                  <span className="text-gray-500">Cutomer: </span>
                  {(data.companies && data.companies.length > 0)
                    ? data.companies.map((c) => `${c.name || '—'} (${c.companyCode ?? '—'})`).join(', ')
                    : '—'}
                </div>
              </Section>

              <Section title="Vehicle / Dock">
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-1">
                  <div><span className="text-gray-500">Vehicle No:</span> {data.vehicleNumber ?? '—'}</div>
                  <div><span className="text-gray-500">Vehicle Temp:</span> {data.vehicleTemperature ?? '—'}</div>
                  <div><span className="text-gray-500">Vehicle Reporting Date:</span> {fmtDate(data.vehicleReportingDate)}</div>
                  <div><span className="text-gray-500">Vehicle Reporting Time:</span> {fmtVehicleReportingTime(data.vehicleReportingTime)}</div>
                  <div><span className="text-gray-500">Product Temperature:</span> {data.productTemperature != null ? data.productTemperature : '—'}</div>
                  <div><span className="text-gray-500">Dock Number:</span> {data.dockNumber ?? '—'}</div>
                  <div><span className="text-gray-500">Dock In Time:</span> {fmtDateTime(data.dockInTime)}</div>
                  <div><span className="text-gray-500">Dock Out Time:</span> {fmtDateTime(data.dockOutTime)}</div>
                  <div><span className="text-gray-500">Location:</span> {data.location ?? '—'}</div>
                </div>
              </Section>

              <Section title="Items">
                {/* {data.totalGrossWeight != null && (
                  <div className="mb-2 text-sm text-gray-700">
                    <span className="text-gray-500">Total Gross Weight:</span>{" "}
                    {Number(data.totalGrossWeight || 0).toFixed(2)} kg
                  </div>
                )} */}
                <div className="overflow-x-auto rounded-xl border border-[#EEF1F5]">
                  <table className="w-full border-collapse text-sm">
                    <thead>
                      <tr className="bg-[#F6F7FA]">
                        <th className="text-left font-semibold text-[#98A2B3] px-3 py-2 rounded-tl-xl">SKU</th>
                        {/* <th className="text-left font-semibold text-[#98A2B3] px-3 py-2">Product Name / Description</th> */}
                        <th className="text-right font-semibold text-[#98A2B3] px-3 py-2">Invoice Qty</th>
                        <th className="text-right font-semibold text-[#98A2B3] px-3 py-2">Received Qty</th>
                        {/* <th className="text-right font-semibold text-[#98A2B3] px-3 py-2">Gross Weight</th>
                        <th className="text-right font-semibold text-[#98A2B3] px-3 py-2 rounded-tr-xl">Qty In Pallet</th> */}
                      </tr>
                    </thead>
                    <tbody>
                      {(data.items && data.items.length > 0) ? data.items.map((item, i) => {
                        const p = item.productId;
                        const sku = p && typeof p === 'object' ? (p.sku ?? p.skuName) : p;
                        // const name = (p && typeof p === 'object' ? (p.description ?? p.skuName) : null) ?? item.productDescription ?? '—';
                        return (
                          <tr key={i} className="border-t border-[#EEF1F5]">
                            <td className="px-3 py-2 text-[#101828]">{sku ?? '—'}</td>
                            {/* <td className="px-3 py-2 text-[#101828]">{name}</td> */}
                            <td className="px-3 py-2 text-right text-[#101828]">{item.invoiceQuantity ?? '—'}</td>
                            <td className="px-3 py-2 text-right text-[#101828]">{item.receivedQuantity ?? '—'}</td>
                            {/* <td className="px-3 py-2 text-right text-[#101828]">{Number(item.grossWeight || 0).toFixed(2)}</td>
                            <td className="px-3 py-2 text-right text-[#101828]">{Number(item.qtyInPallet || 0).toFixed(2)}</td> */}
                          </tr>
                        );
                      }) : (
                        <tr>
                          <td colSpan={6} className="px-3 py-4 text-center text-gray-500">No items</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </Section>

              <Section title="Batches">
                <div className="overflow-x-auto rounded-xl border border-[#EEF1F5]">
                  <table className="w-full border-collapse text-sm">
                    <thead>
                      <tr className="bg-[#F6F7FA]">
                        <th className="text-left font-semibold text-[#98A2B3] px-3 py-2 rounded-tl-xl">Batch Number</th>
                        <th className="text-left font-semibold text-[#98A2B3] px-3 py-2">Mfg Date</th>
                        <th className="text-left font-semibold text-[#98A2B3] px-3 py-2">Exp Date</th>
                        <th className="text-right font-semibold text-[#98A2B3] px-3 py-2">Qty</th>
                        {/* <th className="text-left font-semibold text-[#98A2B3] px-3 py-2">Condition</th> */}
                        {/* <th className="text-left font-semibold text-[#98A2B3] px-3 py-2 rounded-tr-xl">Bin</th> */}
                      </tr>
                    </thead>
                    <tbody>
                      {(data.batches && data.batches.length > 0) ? data.batches.map((batch, i) => {
                        const bin = batch.binId;
                        const binLabel = bin && typeof bin === 'object' ? (bin.binCode ?? bin._id) : (batch.binCode ?? batch.binId ?? '—');
                        return (
                          <tr key={i} className="border-t border-[#EEF1F5]">
                            <td className="px-3 py-2 text-[#101828]">{batch.batchNumber ?? batch.batch_number ?? '—'}</td>
                            <td className="px-3 py-2 text-[#101828]">{fmtDate(batch.manufacturingDate ?? batch.mfgDate)}</td>
                            <td className="px-3 py-2 text-[#101828]">{fmtDate(batch.expiryDate ?? batch.expDate)}</td>
                            <td className="px-3 py-2 text-right text-[#101828]">{batch.quantity ?? '—'}</td>
                            {/* <td className="px-3 py-2 text-[#101828]">{batch.condition ?? '—'}</td> */}
                            {/* <td className="px-3 py-2 text-[#101828]">{binLabel}</td> */}
                          </tr>
                        );
                      }) : (
                        <tr>
                          <td colSpan={5} className="px-3 py-4 text-center text-gray-500">No batches</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </Section>

              <Section title="Meta">
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-1">
                  <div><span className="text-gray-500">GRN Number:</span> {data.grnNumber ?? '—'}</div>
                  <div><span className="text-gray-500">GRN Date:</span> {fmtDate(data.grnDate)}</div>
                  <div><span className="text-gray-500">Created:</span> {fmtDateTime(data.createdAt)}</div>
                  <div><span className="text-gray-500">Updated:</span> {fmtDateTime(data.updatedAt)}</div>
                </div>
              </Section>
            </>
          )}
        </div>
        {!loading && (
          <div className="shrink-0 px-6 py-4 border-t border-[#EEF1F5] flex justify-end">
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl border border-[#D0D5DD] bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(popupContent, document.body);
};

export default InwardDetailsModal;
